import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { User } from "@/features/auth/services/firebase";
import Upload from "@/pages/Upload";
import Dashboard from "@/pages/Dashboard";
import History from "@/pages/History";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Onboarding from "@/pages/Onboarding";
import PendingApproval from "@/pages/PendingApproval";
import InviteAccept from "@/pages/InviteAccept";
import { isFeatureEnabled } from "@/services/feature-flags";
import FeatureFlagRoute from "./FeatureFlagRoute";

const Results = lazy(() => import("@/pages/Results"));
const ReportViewer = lazy(() => import("@/pages/ReportViewer"));
const MonitoringDashboard = lazy(
  () => import("@/pages/MonitoringDashboard"),
);
const PipelineTransparency = lazy(
  () => import("@/pages/PipelineTransparency"),
);

function RouteLoadingFallback() {
  return (
    <div
      className="min-h-[100dvh] bg-[var(--background)]"
      data-test="route-loading"
    />
  );
}

/**
 * Post-login redirect target: send the user back to the page they were
 * on before the forced sign-in (e.g. `/teams/accept?token=...`) instead of
 * dropping them on the home page. ProtectedRoute stores the original
 * `pathname + search` in the router state's `next` field; the home
 * route's unauthenticated redirect does the same.
 */
function AfterLoginRedirect() {
  const location = useLocation();
  const next =
    typeof location.state?.next === "string" ? location.state.next : null;
  // Accept only same-origin paths (starting with "/"), never "#", "?" or a
  // full URL - this value crosses the auth boundary and a stale or forged
  // entry must not be able to point the user somewhere unintended.
  const target =
    next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
  return <Navigate to={target} replace />;
}

interface AppRoutesProps {
  user: User | null;
}

export default function AppRoutes({ user }: AppRoutesProps) {
  return (
    <Routes>
      <Route
        path="/"
        element={user ? <Upload /> : <Navigate to="/login" replace />}
      />
      <Route path="/home" element={<Dashboard />} />
      <Route
        path="/results/:auditId"
        element={(
          <Suspense fallback={<RouteLoadingFallback />}>
            <Results />
          </Suspense>
        )}
      />
      <Route
      // ⚠️ UNREACHABLE IN EVERY DEPLOYED BUILD (pirx-vj26):
      // `scripts/deployment/deploy-frontend.ts` pins VITE_FF_REPORTS=false at
      // build time, so the FeatureFlagRoute wrapping this element
      // redirects /reports/:id to /history in all deployed environments.
      // The ReportViewer page, its
      // polling hook, downloadAssessmentPdf, and the backend
      // `GET /api/assessments/:id/pdf` endpoint are therefore effectively
      // dead in production — a code-level caller is NOT proof this route
      // is live. Before deleting this route + ReportViewer.tsx + the /pdf
      // backend endpoint (one coordinated pass, mirroring the report-DOCX
      // cleanup), confirm with the product owner that the REPORTS flag is
      // permanently retired; if it may come back, keep the dead code and
      // this note. See the banner at the top of ReportViewer.tsx.
        path="/reports/:id"
        element={(
          <FeatureFlagRoute flag="REPORTS" fallbackPath="/history">
            <Suspense fallback={<RouteLoadingFallback />}>
              <ReportViewer />
            </Suspense>
          </FeatureFlagRoute>
        )}
      />
      <Route path="/history" element={<History />} />
      <Route
        path="/internal/pipeline-transparency/:auditId"
        element={(
          <Suspense fallback={<RouteLoadingFallback />}>
            <PipelineTransparency />
          </Suspense>
        )}
      />
      <Route
        path="/monitoring"
        element={(
          <FeatureFlagRoute flag="WEBSITE_MONITORING" fallbackPath="/">
            <Suspense fallback={<RouteLoadingFallback />}>
              <MonitoringDashboard />
            </Suspense>
          </FeatureFlagRoute>
        )}
      />
      <Route
        path="/monitoring/new"
        element={(
          <FeatureFlagRoute flag="WEBSITE_MONITORING" fallbackPath="/">
            <Suspense fallback={<RouteLoadingFallback />}>
              <MonitoringDashboard />
            </Suspense>
          </FeatureFlagRoute>
        )}
      />
      <Route
        path="/monitoring/:id"
        element={(
          <FeatureFlagRoute flag="WEBSITE_MONITORING" fallbackPath="/">
            <Suspense fallback={<RouteLoadingFallback />}>
              <MonitoringDashboard />
            </Suspense>
          </FeatureFlagRoute>
        )}
      />
      <Route path="/assessments/*" element={<Navigate to="/history" replace />} />
      {isFeatureEnabled("KNOWLEDGE_BASE") && (
        <Route path="/knowledge-base" element={<KnowledgeBase />} />
      )}
      <Route path="/settings" element={<Settings />} />
      <Route
        path="/teams/accept"
        element={(
          <FeatureFlagRoute flag="TEAMS" fallbackPath="/">
            <InviteAccept />
          </FeatureFlagRoute>
        )}
      />
      <Route path="/login" element={user ? <AfterLoginRedirect /> : <Login />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/pending-approval" element={<PendingApproval />} />
    </Routes>
  );
}
